import { LoadingIcon } from '@stormwater-software/lib-ui';
import classNames from 'classnames';
import dayjs from 'dayjs';
import EXIF from 'exif-js';
import React, { useContext, useState } from 'react';
import Dropzone from 'react-dropzone';
import { useQuery } from 'react-query';
import { ProjectContext } from '../../../contexts/ProjectContext';
import TenantFeatureContext from '../../../contexts/TenantContext';
import clientApi from '../../../utils/api/client';
import uploadApi from '../../../utils/api/upload';
import {
  FileSizeValidation,
  FileTypeValidation,
  ImageDimensionValidation,
  UploadFilesValidation,
  UploadFileValidation,
} from '../../../utils/file';
import { dmsToDecimal, getCurrentPosition } from '../../../utils/googlemaps';
import { resizePhotos } from '../../../utils/resizePhoto';
import SuccessModal from '../../Shared/SuccessModal/SuccessModal';

/** Photo location metadata, stored on the upload record. */
export type PhotoGps = { lat: number; lon: number; timestamp: string | null };

const uploadFilesValidation = UploadFilesValidation({
  uploadFileValidation: UploadFileValidation({
    fileSizeValidation: FileSizeValidation(),
    fileTypeValidation: FileTypeValidation(),
    imageDimensionValidation: ImageDimensionValidation(),
  } as any),
});

/** A photo modified this recently is taken to be the shot just captured. */
const RECENT_CAPTURE_MS = 2 * 60 * 1000;

const METADATA_FIELD = 'enable_photo_metadata_requirement';

const isImage = (file: File) => !!file.type && file.type.startsWith('image/');

/** EXIF `DateTime` ("2024:01:31 10:22:33") -> ISO string the API can store. */
const exifDateToIso = (raw?: string) => {
  if (!raw) return null;
  const parsed = dayjs(
    raw.replace(':', '-').replace(':', '-').replace(' ', 'T'),
  );

  return parsed.isValid() ? parsed.toISOString() : null;
};

// A photo's location metadata: EXIF GPS tags, or the device position for a
// recent capture. null when the photo has neither.
const readPhotoGps = (file: File) =>
  new Promise<PhotoGps | null>((resolve) => {
    EXIF.getData(file as any, function (this: any) {
      const gpsLat = EXIF.getTag(this, 'GPSLatitude');
      const gpsLon = EXIF.getTag(this, 'GPSLongitude');

      if (gpsLat && gpsLon) {
        resolve({
          lat: dmsToDecimal(gpsLat, EXIF.getTag(this, 'GPSLatitudeRef')),
          lon: dmsToDecimal(gpsLon, EXIF.getTag(this, 'GPSLongitudeRef')),
          timestamp: exifDateToIso(EXIF.getTag(this, 'DateTime')),
        });

        return;
      }

      const justCaptured =
        !!file.lastModified &&
        Date.now() - file.lastModified < RECENT_CAPTURE_MS;

      if (!justCaptured) {
        resolve(null);

        return;
      }

      getCurrentPosition()
        .then(({ latitude, longitude }: any) =>
          resolve({
            lat: latitude,
            lon: longitude,
            timestamp: new Date().toISOString(),
          }),
        )
        .catch(() => resolve(null));
    });
  });

interface IAssetUploadInputProps {
  allowedTypes: string[];
  maxFileSize: number;
  imageDimensions: { width: number; height: number };
  className?: string;
  onUpload: (upload: { id: number; mime_type?: string }) => void;
}

/** Asset file upload control with drop-and-upload and photo metadata checks. */
const AssetUploadInput: React.FC<IAssetUploadInputProps> = ({
  allowedTypes,
  maxFileSize,
  imageDimensions,
  className,
  onUpload,
}) => {
  const projectContext = useContext(ProjectContext);
  const tenantFeatureContext = useContext(TenantFeatureContext);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const project = projectContext?.project;
  const metadataEnabled =
    tenantFeatureContext.tenantHasFeature(
      'Enable Photo Metadata Requirement',
    ) &&
    !!project?.client_id &&
    !!project?.[METADATA_FIELD];
  const clientFields = useQuery(
    ['clientCustomFields', project?.client_id, METADATA_FIELD],
    () => clientApi.getCustomFields(project!.client_id, [METADATA_FIELD]),
    { enabled: metadataEnabled, staleTime: Infinity },
  );
  const requirePhotoMetadata =
    metadataEnabled && !!clientFields.data?.[METADATA_FIELD];

  const fail = (messages: string[]) => {
    setErrorMessages(messages);
    setIsLoading(false);
  };

  const onDrop = async (files: File[]) => {
    setIsLoading(true);

    const errors = await uploadFilesValidation.validate(files, {
      maxFileSize,
      allowedTypes,
      imageDimensions,
    });

    if (errors.length) {
      return fail(errors.map((error: { message: string }) => error.message));
    }

    const gpsByFile = new Map<File, PhotoGps | null>();

    if (requirePhotoMetadata) {
      const photos = files.filter(isImage);
      const gpsData = await Promise.all(photos.map(readPhotoGps));

      photos.forEach((file, index) => gpsByFile.set(file, gpsData[index]));

      const missing = photos.filter((file) => !gpsByFile.get(file));

      if (missing.length) {
        return fail([
          'This project requires photos to contain location metadata.',
          `Please remove or replace: ${missing
            .map((file) => file.name)
            .join(', ')}`,
        ]);
      }
    }

    try {
      // SW2-2440: GPS was read from the originals above; the canvas drops EXIF.
      const resized = await resizePhotos(files);

      await Promise.all(
        files.map(async (file, index) =>
          onUpload(
            await uploadApi.create(resized[index], null, gpsByFile.get(file)),
          ),
        ),
      );
    } catch (err) {
      return fail(['Failed to upload file.']);
    }

    setIsLoading(false);
  };

  return (
    <>
      <Dropzone onDrop={onDrop} multiple>
        {({ getRootProps, getInputProps, isDragActive }) => (
          <div
            {...getRootProps()}
            className={classNames('upload-control dropzone', className, {
              'upload-control--active': isDragActive,
              'upload-control--loading': isLoading,
            })}
          >
            <input {...getInputProps()} />
            {isLoading ? (
              <LoadingIcon className='upload-control__icon' />
            ) : (
              <i className='fa fa-cloud-upload upload-control__icon' />
            )}
          </div>
        )}
      </Dropzone>

      <SuccessModal
        show={!!errorMessages.length}
        handleClose={() => setErrorMessages([])}
        handleSubmit={() => setErrorMessages([])}
        submitBtnText='OK'
        messages={errorMessages}
        title='Error'
        isAlert
      />
    </>
  );
};

export default AssetUploadInput;
